import { Location } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideEllipsisVertical, lucidePen, lucidePlus, lucideTrash } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { filter } from 'rxjs';

import { ConfirmDialogComponent } from '../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { PaginateurComponent } from '../../../shared/components/paginateur/paginateur.component';
import { SidepanelService } from '../../../shared/components/sidepanel/sidepanel.service';
import { TechniqueAnimation } from '../model/technique-animation.model';
import { TechniqueAnimationStore } from '../store/technique-animation.store';
import { FormTechniqueAnimationComponent } from './form-technique-animation/form-technique-animation.component';
import { HlmFieldImports } from '@spartan-ng/helm/field';

@Component({
  selector: 'app-technique-animation-list',
  imports: [
    PaginateurComponent,
    HlmButtonImports,
    HlmTooltipImports,
    HlmIconImports,
    HlmCardImports,
    ReactiveFormsModule,
    HlmInputImports,
    HlmLabelImports,
    HlmTableImports,
    HlmDropdownMenuImports,
    HlmFieldImports,
  ],
  templateUrl: './technique-animation-list.component.html',
  providers: [provideIcons({ lucideTrash, lucidePen, lucidePlus, lucideArrowLeft, lucideEllipsisVertical })],
})
export class TechniqueAnimationListComponent {
  readonly store = inject(TechniqueAnimationStore);
  readonly location = inject(Location);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialog = inject(HlmDialogService);
  readonly #sidepanelService = inject(SidepanelService);

  constructor() {
    this.store.charger();
  }

  ajouterTechnique() {
    this.#sidepanelService.setComponentToDisplay(
      "Ajouter une technique d'animation",
      FormTechniqueAnimationComponent,
      `Vous pouvez créer une nouvelle technique d’animation uniquement, ou en créer plusieurs d’un seul coup. Le lien vers le PDF correspond au support de la technique d’animation.`,
    );
    this.#sidepanelService.open();
  }

  modifierTechnique(techniqueAnimation: TechniqueAnimation) {
    this.#sidepanelService.setComponentToDisplay(
      "Modifier la technique d'animation",
      FormTechniqueAnimationComponent,
      `Vous pouvez modifier la technique d’animation sélectionnée.`,
      { techniqueAnimation },
    );
    this.#sidepanelService.open();
  }

  supprimerTechnique(technique: TechniqueAnimation) {
    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: `Êtes-vous sûr de vouloir supprimer ${technique.libelle} ?`,
        contenu: `Vous êtes sur le point de supprimer <b>${technique.libelle}</b>. Cette action est définitive et ne peut pas être annulée.`,
      },
    });
    dialogRef.closed$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((result: boolean) => result),
      )
      .subscribe(async () => {
        this.store.supprimer(technique.id);
      });
  }
}
